import { NetworkAdapter } from '@teamworksdev/loco-sync-client';
import { isTRPCClientError, getTrpcProxyClient } from '../../trpc';
import { MS } from '@strength/loco-sync';
import { environment } from '../../environment';
import { createWebSocketClient } from './websocket';
import { sendTransaction_BlockDraftAsync } from './block-draft-async';
import { sendTransaction_PublishedWorkoutStatusAsync } from './published-workout-status-async';
import { sendTransaction_BlockAsync } from './block-async';
import { BlockDraftWorkoutDomain } from '@strength/domain';
import { t } from 'i18next';
import { toast } from '../../app/hooks/useToast';

export const createNetworkAdapter = (
  onAuthError: () => void,
): NetworkAdapter<MS> => {
  // The client may carry the tw-team-ids header, but every sync route
  // (bootstrap, deltaSync, the *.save mutations) is an orgProcedure that never
  // reads it — sync is org-scoped server-side — so the header is inert here.
  const trpc = getTrpcProxyClient(onAuthError);

  return {
    sendTransaction: async (args) => {
      try {
        let result: { lastSyncId: number };
        switch (args.module) {
          case 'Block':
            result = await sendTransaction_BlockAsync(trpc, args);
            break;
          case 'BlockDraftAsync':
            result = await sendTransaction_BlockDraftAsync(trpc, args);
            break;
          case 'BlockDraftWorkout':
            result = await trpc.block.draftWorkout.save.mutate(args);
            break;
          case 'AthleteDraftWorkout':
            result = await trpc.block.draftWorkout.athlete.save.mutate(args);
            break;
          case 'AthleteActualWorkout':
            result = await trpc.actuals.actualWorkout.save.mutate(args);
            break;
          case 'AthleteModifiedPublishedWorkout':
            result =
              await trpc.actuals.modifiedPublishedWorkout.save.mutate(args);
            break;
          case 'PublishedWorkoutStatus':
            result = await sendTransaction_PublishedWorkoutStatusAsync(
              trpc,
              args,
            );
            break;
          default:
            const _: never = args;
            throw new Error(`Unknown args type: ${(args as any).type}`);
        }

        return {
          ok: true,
          value: result,
        };
      } catch (e) {
        return handleMaybeTRPCError(e);
      }
    },
    deltaSync: async (fromSyncId, toSyncId) => {
      try {
        let currentFromSyncId = fromSyncId;
        const syncActions = [];
        while (currentFromSyncId < toSyncId) {
          const { sync, nextFromSyncId } = await trpc.deltaSync.query({
            dbHash: environment.VITE_DB_HASH,
            fromSyncId: currentFromSyncId,
            toSyncId,
          });

          syncActions.push(...sync);

          if (!nextFromSyncId) break;
          currentFromSyncId = nextFromSyncId;
        }

        return {
          ok: true,
          value: { sync: syncActions },
        };
      } catch (e) {
        return handleMaybeTRPCError(e);
      }
    },
    bootstrap: async (args) => {
      try {
        const value = await trpc.bootstrap.query({
          dbHash: environment.VITE_DB_HASH,
          ...args,
        });

        return {
          ok: true,
          value: {
            bootstrap: value.bootstrap,
            firstSyncId: value.firstSyncId,
            syncGroups: value.syncGroups,
          },
        };
      } catch (e) {
        return handleMaybeTRPCError(e);
      }
    },
    initSync: async (callback) => {
      return createWebSocketClient(
        environment.VITE_WS_URL,
        (msg) => {
          switch (msg.type) {
            case 'auth_error':
              onAuthError();
              return;
            case 'error':
              console.error('WebSocket error:', msg.message);
              return;
            case 'pong':
              // Do nothing; this is already handled
              return;
            default:
              callback(msg);
          }
        },
        {
          onOpen: (socket) => {
            // TODO: Would be great to have the API contract here defined somewhere
            socket.send(
              JSON.stringify({
                dbHash: environment.VITE_DB_HASH,
                type: 'handshake',
              }),
            );
          },
        },
      );
    },
  };
};

const handleMaybeTRPCError = (e: any) => {
  if (isTRPCClientError(e) && e.message.includes('NetworkError')) {
    return {
      ok: false,
      error: 'network',
    } as const;
  }
  // SC-5512: the server's slot-conflict guard rejected a create that would
  // bind two grid cells to one day document (a concurrent editor claimed the
  // slot first). The loco-sync pipeline rolls the optimistic create back and
  // only reports a bare 'server' error upstream, so surface the conflict
  // here, where the typed message is still visible.
  if (
    isTRPCClientError(e) &&
    e.message.includes(BlockDraftWorkoutDomain.SLOT_OCCUPIED_ERROR)
  ) {
    toast({
      title: t('workoutSlotConflict'),
      severity: 'error',
    });
  }
  return {
    ok: false,
    error: 'server',
  } as const;
};
